/**
 * Reusable modal for Razorpay payment flow with state-based views.
 * All states rendered inline: processing, pending (iframe), success, failed.
 * Backdrop blur, scale + fade animation; close disabled while processing.
 */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const PAYMENT_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export default function RazorPayPaymentModal({
  open,
  onClose,
  paymentUrl,
  title = 'Complete payment',
  paymentStatus = PAYMENT_STATUS.PENDING,
  totalPayable,
  onRetry,
  onRefreshStatus,
  onGoToSubscription,
  onChangeMethod,
  isRefreshingStatus = false,
  autoReloadOnSuccess = true,
  reloadDelayMs = 2500,
  successTitle = 'Subscription Upgraded to Pro',
  successMessage = 'Reloading your account…',
}) {
  const isProcessing = paymentStatus === PAYMENT_STATUS.PROCESSING;
  const canClose = !isProcessing;

  const [iframeLoading, setIframeLoading] = useState(true);
  const handleIframeLoad = useCallback(() => setIframeLoading(false), []);
  const handleOpenInNewTab = useCallback(() => {
    if (paymentUrl) window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  }, [paymentUrl]);

  const handleClose = useCallback(() => {
    if (!canClose) return;
    onClose?.();
  }, [canClose, onClose]);

  // Reset iframe loading when modal opens or payment URL changes.
  useEffect(() => {
    if (open && paymentUrl) setIframeLoading(true);
  }, [open, paymentUrl]);

  // Enter animation: scale + fade (subtle).
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) {
      setEntered(false);
      const t = setTimeout(() => setEntered(true), 20);
      return () => clearTimeout(t);
    }
    setEntered(false);
  }, [open]);

  // Success screen: slow fade-in (state-driven so it works with MUI)
  const [successVisible, setSuccessVisible] = useState(false);
  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      setSuccessVisible(false);
      const t = setTimeout(() => setSuccessVisible(true), 50);
      return () => clearTimeout(t);
    }
    setSuccessVisible(false);
  }, [paymentStatus]);

  // ---- NEW: Auto reload after success ----
  const reloadTimerRef = useRef(null);

  useEffect(() => {
    if (!open || paymentStatus !== PAYMENT_STATUS.SUCCESS || !autoReloadOnSuccess) return;

    reloadTimerRef.current = setTimeout(() => {
      window.location.reload();
    }, reloadDelayMs);

    return () => {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
  }, [open, paymentStatus, autoReloadOnSuccess, reloadDelayMs]);

  if (!open) return null;

  const isResultState = paymentStatus === PAYMENT_STATUS.SUCCESS || paymentStatus === PAYMENT_STATUS.FAILED;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="razorpay-modal-title"
      sx={{
        '& .MuiBackdrop-root': {
          bgcolor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'opacity 0.25s ease',
        },
        '& .razorpay-modal-paper': {
          transition: 'transform 0.25s ease, opacity 0.25s ease',
        },
      }}
    >
      <Box
        className="razorpay-modal-paper"
        sx={{
          position: 'absolute',
          ...(isResultState
            ? {
                top: '50%',
                left: '50%',
                width: '90%',
                maxWidth: 420,
                maxHeight: '90vh',
                borderRadius: 2,
                transform: entered ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.98)',
                '@media (prefers-reduced-motion: reduce)': {
                  transform: 'translate(-50%, -50%) scale(1)',
                },
              }
            : {
                top: 0,
                left: '50%',
                width: '70vw',
                maxWidth: '100%',
                height: '100vh',
                maxHeight: '100vh',
                borderRadius: 0,
                transform: entered ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0.98)',
                '@media (prefers-reduced-motion: reduce)': {
                  transform: 'translateX(-50%) scale(1)',
                },
              }),
          bgcolor: 'background.paper',
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          opacity: entered ? 1 : 0,
          transition: 'opacity 0.2s ease, transform 0.2s ease',
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            opacity: 1,
          },
        }}
      >
        {/* Header: only show for payment/iframe states; hidden on success/failure for cleaner result view */}
        {!isResultState && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Typography id="razorpay-modal-title" variant="subtitle1" fontWeight={600} noWrap>
                {title}
              </Typography>
              {totalPayable != null && totalPayable !== '' && (
                <Typography variant="body2" color="primary.main" fontWeight={600}>
                  {totalPayable}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Close"
              disabled={!canClose}
              sx={{
                ml: 1,
                opacity: canClose ? 1 : 0.5,
                cursor: canClose ? 'pointer' : 'not-allowed',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        {/* Body: state-based view — payment iframe rendered directly in modal */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {paymentStatus === PAYMENT_STATUS.PROCESSING && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, p: 3 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Processing…
              </Typography>
            </Box>
          )}

          {paymentStatus === PAYMENT_STATUS.PENDING && (
            <>
              {paymentUrl ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    {iframeLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'background.default',
                          zIndex: 1,
                        }}
                      >
                        <CircularProgress size={40} sx={{ mb: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading payment…
                        </Typography>
                      </Box>
                    )}
                    <Box
                      component="iframe"
                      src={paymentUrl}
                      onLoad={handleIframeLoad}
                      title={title}
                      referrerPolicy="no-referrer-when-downgrade"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      py: 1,
                      px: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      If the form doesn&apos;t appear above,
                    </Typography>
                    <Button size="small" startIcon={<OpenInNewIcon />} onClick={handleOpenInNewTab}>
                      Open in new tab
                    </Button>
                    {onRefreshStatus && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isRefreshingStatus ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={onRefreshStatus}
                        disabled={isRefreshingStatus}
                      >
                        {isRefreshingStatus ? 'Checking…' : 'Refresh status'}
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No payment link available.
                  </Typography>
                  {onRefreshStatus && (
                    <Button size="small" onClick={onRefreshStatus} sx={{ mt: 1.5 }}>
                      Refresh status
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}

          {paymentStatus === PAYMENT_STATUS.SUCCESS && (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                p: 3,
                pt: 4,
                textAlign: 'center',
                opacity: successVisible ? 1 : 0,
                transform: successVisible ? 'scale(1)' : 'scale(0.96)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  opacity: 1,
                  transform: 'scale(1)',
                },
              }}
            >
              <IconButton
                aria-label="Close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              {/* Success badge */}
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2.5,
                  boxShadow: '0 0 0 8px rgba(46, 125, 50, 0.12), 0 4px 20px rgba(46, 125, 50, 0.2)',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 52 }} />
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.75 }}>
                {successTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {successMessage}
              </Typography>

              {/* Optional manual action */}
              {!autoReloadOnSuccess && onGoToSubscription && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={onGoToSubscription}
                  sx={{
                    minWidth: 220,
                    py: 1.25,
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 2,
                    '&:hover': { boxShadow: 3 },
                  }}
                >
                  Go to Subscription
                </Button>
              )}

              <Typography
                component="button"
                type="button"
                onClick={onGoToSubscription}
                variant="body2"
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                Close
              </Typography>
            </Box>
          )}

          {paymentStatus === PAYMENT_STATUS.FAILED && (
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, p: 3, pt: 4, textAlign: 'center' }}>
              <IconButton
                aria-label="Close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                Payment incomplete
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Payment was cancelled or could not be completed.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                {onRetry && (
                  <Button variant="contained" onClick={onRetry}>
                    Retry
                  </Button>
                )}
                <Button variant="outlined" onClick={onChangeMethod ?? handleClose}>
                  Change method
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export { PAYMENT_STATUS };
